import { useFlags } from 'launchdarkly-react-client-sdk'
import { createContext, ReactNode, useContext, useMemo } from 'react'

export const ORGANIZATIONS = {
  pcoms: 'org_G2pgB4ADsuWZ2aNK',
  medgeo: 'org_OM4vswifgUpIYcu1',
  adventist: 'org_GR1VqYoVzjaaiJns',
  ridgewood: 'org_t05y14V3XiElOuJI',
}

type TOrganizationSettingsProviderProps = { children: ReactNode }

type TOrganizationContext = {
  physiciansTitle: string
  showPhysiciansMobile: boolean
  showPhysiciansSpeciality: boolean
  showScrubTechnician: boolean
  enableSmsToReferringPhysicians: boolean
  enableConsumablesAutopopulate: boolean
  enableSpecialRequest: boolean
  enableDictation: boolean
  showPascPreferenceCardFeatures: boolean
}

const OrganizationSettingsContext = createContext<
  TOrganizationContext | undefined
>(undefined)

const OrganizationSettingsProvider = ({
  children,
}: TOrganizationSettingsProviderProps) => {
  const physiciansTitle = 'Physicians'
  const showPhysiciansSpeciality = useFlags().showPhysiciansSpeciality || false
  const showPhysiciansMobile = useFlags().showPhysiciansMobile || false
  const showScrubTechnician = useFlags().showScrubTechnician || false
  const enableSmsToReferringPhysicians =
    useFlags().enableSmsToReferringPhysicians || false
  const enableConsumablesAutopopulate =
    useFlags().enableConsumablesAutopopulate || false
  const enableSpecialRequest = useFlags().enableSpecialRequest || false
  const enableDictation = useFlags().enableDictation || false
  const showPascPreferenceCardFeatures = Boolean(
    useFlags().showPascPreferenceCardFeatures
  )

  const value = useMemo(
    () => ({
      showPhysiciansSpeciality,
      physiciansTitle,
      showPhysiciansMobile,
      showScrubTechnician,
      enableSmsToReferringPhysicians,
      enableConsumablesAutopopulate,
      enableSpecialRequest,
      enableDictation,
      showPascPreferenceCardFeatures,
    }),
    [
      physiciansTitle,
      showPhysiciansSpeciality,
      showPhysiciansMobile,
      showScrubTechnician,
      enableSmsToReferringPhysicians,
      enableConsumablesAutopopulate,
      enableSpecialRequest,
      enableDictation,
      showPascPreferenceCardFeatures,
    ]
  )

  return (
    <OrganizationSettingsContext.Provider value={value}>
      {children}
    </OrganizationSettingsContext.Provider>
  )
}

const useOrganizationSettings = () => {
  const context = useContext(OrganizationSettingsContext)

  if (context === undefined)
    throw new Error(
      'useOrganizationSettings must be used within OrganizationSettingsProvider'
    )

  return context
}

export { OrganizationSettingsProvider, useOrganizationSettings }
